import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, storage } from '../config/firebase';
import { authStore } from '../modules/auth/auth.store';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { showToast } from '../shared/components/Toast';
import { updateProfile } from 'firebase/auth';
import { escapeHTML } from '../shared/lib/format';
import { router } from '../app/router';

/* ------------------------------------------------------------------ */
// Types
/* ------------------------------------------------------------------ */

interface ProfileData {
  photoUrls?: string[];
  age?: number;
  bio?: string;
  city?: string;
  tribe?: string;
  religion?: string;
  relationshipIntent?: string;
  gender?: string;
  whatsappNumber?: string;
  interests?: string[];
  voiceNoteUrl?: string;
  moderationStatus?: 'draft' | 'submitted' | 'approved' | 'rejected';
}

interface SelectOption {
  value: string;
  label: string;
}

interface SettingsContainer extends HTMLElement {
  __settingsCleanup?: () => void;
  __settingsRenderToken?: symbol;
}

/* ------------------------------------------------------------------ */
// Constants
/* ------------------------------------------------------------------ */

const GENDERS: SelectOption[] = [
  { value: '', label: 'Select gender' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const TRIBES: SelectOption[] = [
  { value: '', label: 'Select tribe' },
  { value: 'Yoruba', label: 'Yoruba' },
  { value: 'Igbo', label: 'Igbo' },
  { value: 'Hausa', label: 'Hausa' },
  { value: 'Edo', label: 'Edo' },
  { value: 'Ijaw', label: 'Ijaw' },
  { value: 'Other', label: 'Other' },
];

const RELIGIONS: SelectOption[] = [
  { value: '', label: 'Select religion' },
  { value: 'Christian', label: 'Christian' },
  { value: 'Muslim', label: 'Muslim' },
  { value: 'Other', label: 'Other' },
];

const INTENTS: SelectOption[] = [
  { value: '', label: 'Select intent' },
  { value: 'Marriage', label: 'Marriage' },
  { value: 'Serious Relationship', label: 'Serious Relationship' },
  { value: 'Casual Dating', label: 'Casual Dating' },
  { value: 'Friendship', label: 'Friendship' },
];

/* ------------------------------------------------------------------ */
// Helpers
/* ------------------------------------------------------------------ */

function formatMemberSince(dateString: string | undefined): string {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
    });
  } catch {
    return '—';
  }
}

function buildSelectOptions(
  options: SelectOption[],
  selectedValue: string,
): string {
  return options
    .map(
      (o) =>
        `<option value="${escapeHTML(o.value)}" ${
          o.value === selectedValue ? 'selected' : ''
        }>${escapeHTML(o.label)}</option>`,
    )
    .join('');
}

/* ------------------------------------------------------------------ */
// Sections
/* ------------------------------------------------------------------ */

function renderLoadingSkeleton(): string {
  return `
    <main class="section pt-nav">
      <div class="container max-w-2xl">
        <div class="skeleton skeleton-title mb-4"></div>
        <div class="skeleton skeleton-text mb-8"></div>
        <div class="card card-body mb-6">
          <div class="flex gap-6">
            <div class="skeleton skeleton-avatar"></div>
            <div class="flex-1 space-y-3">
              <div class="skeleton skeleton-text w-1/3"></div>
              <div class="skeleton skeleton-text w-1/2"></div>
              <div class="skeleton skeleton-badge w-1/4"></div>
            </div>
          </div>
        </div>
        <div class="card card-body mb-6">
          <div class="skeleton skeleton-text w-1/4 mb-3"></div>
          <div class="skeleton skeleton-text w-full"></div>
          <div class="skeleton skeleton-text w-3/4 mt-2"></div>
        </div>
      </div>
    </main>
  `;
}

function renderProfileHeader(
  name: string,
  email: string,
  age: number | null,
  photoUrl: string,
  isPremium: boolean,
  isVerified: boolean,
  tags: { icon: string; label: string }[],
): string {
  const avatar = photoUrl
    ? `<img src="${escapeHTML(photoUrl)}" alt="${escapeHTML(name)}" class="profile-avatar-img" loading="lazy">`
    : `<div class="profile-avatar-placeholder" aria-hidden="true"><i class="fa-solid fa-user text-5xl text-muted"></i></div>`;

  const premiumBadge = isPremium
    ? `<span class="badge badge-verified"><i class="fa-solid fa-crown" aria-hidden="true"></i> Zuri VIP</span>`
    : `<span class="badge badge-accent">Free Member</span>`;

  const verifiedBadge = isVerified
    ? `<span class="badge badge-success"><i class="fa-solid fa-circle-check" aria-hidden="true"></i> Verified</span>`
    : '';

  const tagHtml = tags.length
    ? `<div class="profile-tags">${tags
        .map(
          (t) =>
            `<div class="tag"><i class="fa-solid ${t.icon}" aria-hidden="true"></i> ${escapeHTML(t.label)}</div>`,
        )
        .join('')}</div>`
    : '';

  return `
    <div class="card card-body mb-6">
      <div class="profile-header">
        <div class="profile-avatar" role="img" aria-label="Profile photo of ${escapeHTML(name)}">
          ${avatar}
        </div>
        <div class="profile-meta">
          <h2 class="profile-name">${escapeHTML(name)}${age !== null ? `, ${age}` : ''}</h2>
          <p class="profile-email text-muted">${escapeHTML(email)}</p>
          <div class="profile-badges flex gap-2 mb-3" style="flex-wrap:wrap">
            ${premiumBadge}
            ${verifiedBadge}
          </div>
          ${tagHtml}
        </div>
      </div>
    </div>
  `;
}

function renderBioSection(bio: string, interests: string[]): string {
  const interestTags =
    interests.length > 0
      ? `<div class="mt-4">
           <h4 class="text-sm text-muted mb-2">Interests</h4>
           <div class="flex gap-2 flex-wrap">
             ${interests.map((i) => `<span class="tag">${escapeHTML(i)}</span>`).join('')}
           </div>
         </div>`
      : '';

  return `
    <div class="card card-body mb-6">
      <div class="flex items-center justify-between mb-3">
        <h3 class="m-0">About Me</h3>
        <button class="btn btn-outline btn-sm" id="editProfileBtn" aria-haspopup="dialog" aria-controls="editProfileModal">
          <i class="fa-solid fa-pen-to-square" aria-hidden="true"></i> Edit Profile
        </button>
      </div>
      <p class="text-secondary">${escapeHTML(bio || 'No bio yet — complete your profile below.')}</p>
      ${interestTags}
    </div>
  `;
}

function renderVoicePlayer(voiceUrl: string): string {
  return `
    <div class="card card-body mb-6">
      <h3 class="mb-3">Voice Intro</h3>
      <div class="audio-player">
        <button 
          class="audio-play-btn" 
          id="profileVoicePlayBtn" 
          data-url="${escapeHTML(voiceUrl)}"
          aria-label="Play voice intro"
        >
          <i class="fa-solid fa-play" aria-hidden="true"></i>
        </button>
        <span class="audio-label">Play your voice intro</span>
      </div>
    </div>
  `;
}

function renderActions(isPremium: boolean, moderationStatus?: ProfileData['moderationStatus']): string {
  const membershipBtn = isPremium
    ? `<button class="btn btn-secondary w-full" id="viewPlansBtn">
         <i class="fa-solid fa-crown" aria-hidden="true"></i> Manage Membership
       </button>`
    : `<button class="btn btn-primary w-full" id="viewPlansBtn">
         <i class="fa-solid fa-crown" aria-hidden="true"></i> Upgrade to Premium
       </button>`;

  return `
    <div class="flex flex-col gap-4 mb-6">
      ${membershipBtn}
      ${moderationStatus === 'submitted'
        ? `<p class="text-sm text-muted text-center">Your profile is awaiting moderation review.</p>`
        : moderationStatus === 'approved'
          ? `<p class="text-sm text-success text-center">Your profile is live in discovery.</p>`
          : `<button class="btn btn-secondary w-full" id="submitProfileBtn">
               <i class="fa-solid fa-paper-plane" aria-hidden="true"></i> Submit Profile for Review
             </button>`}
      <div class="divider pt-4 mt-2">
        <button class="btn btn-ghost w-full text-error" id="signOutBtn">
          <i class="fa-solid fa-right-from-bracket" aria-hidden="true"></i> Sign Out
        </button>
        <button class="btn btn-ghost w-full text-error" id="deleteAccountBtn">
          <i class="fa-solid fa-trash" aria-hidden="true"></i> Delete Account
        </button>
      </div>
    </div>
  `;
}

function renderEditModal(profile: ProfileData, name: string): string {
  const genderOptions = buildSelectOptions(GENDERS, profile.gender || '');
  const tribeOptions = buildSelectOptions(TRIBES, profile.tribe || '');
  const religionOptions = buildSelectOptions(RELIGIONS, profile.religion || '');
  const intentOptions = buildSelectOptions(INTENTS, profile.relationshipIntent || '');

  return `
    <div 
      class="modal-overlay" 
      id="editProfileModal" 
      role="dialog" 
      aria-modal="true" 
      aria-labelledby="edit-modal-title"
      hidden
    >
      <div class="modal-card max-w-lg">
        <button 
          class="modal-close" 
          id="editProfileModalClose"
          aria-label="Close edit profile dialog"
        >
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
        <h3 id="edit-modal-title" class="mb-2">Edit Your Profile</h3>
        <p class="text-sm text-muted mb-6">Update your profile information visible on Zuri discovery.</p>

        <form id="editProfileForm" class="flex flex-col gap-4" novalidate>
          <div class="grid grid-2 gap-4">
            <div class="form-group">
              <label class="form-label" for="editName">Display Name</label>
              <input type="text" id="editName" class="form-input" value="${escapeHTML(name)}" required>
            </div>
            <div class="form-group">
              <label class="form-label" for="editAge">Age</label>
              <input type="number" id="editAge" class="form-input" value="${profile.age || ''}" min="18" max="99" required>
              <span class="form-hint">Must be 18 or older</span>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label" for="editGender">Gender</label>
            <select id="editGender" class="form-select" required>
              ${genderOptions}
            </select>
          </div>

          <div class="grid grid-2 gap-4">
            <div class="form-group">
              <label class="form-label" for="editCity">City / State</label>
              <input type="text" id="editCity" class="form-input" value="${escapeHTML(profile.city || '')}" placeholder="e.g. Lagos (V.I.)" required>
            </div>
            <div class="form-group">
              <label class="form-label" for="editWhatsapp">WhatsApp Number</label>
              <input 
                type="tel" 
                id="editWhatsapp" 
                class="form-input" 
                value="${escapeHTML(profile.whatsappNumber || '')}" 
                placeholder="+234 800 000 0000"
                pattern="^[\+]?[0-9\s\-\(\)]{10,15}$"
                title="Enter a valid Nigerian phone number, e.g. +234 800 000 0000"
              >
            </div>
          </div>

          <div class="grid grid-3 gap-4">
            <div class="form-group">
              <label class="form-label" for="editTribe">Tribe</label>
              <select id="editTribe" class="form-select">
                ${tribeOptions}
              </select>
            </div>
            <div class="form-group">
              <label class="form-label" for="editReligion">Religion</label>
              <select id="editReligion" class="form-select">
                ${religionOptions}
              </select>
            </div>
            <div class="form-group">
              <label class="form-label" for="editIntent">Intent</label>
              <select id="editIntent" class="form-select">
                ${intentOptions}
              </select>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label" for="editPhotoFile">Profile Photo</label>
            <input type="file" id="editPhotoFile" class="form-input" accept="image/jpeg,image/png,image/webp">
            <span class="form-hint">JPEG, PNG, or WebP, up to 10 MB.</span>
          </div>

          <div class="form-group">
            <label class="form-label" for="editVoiceFile">Voice Intro</label>
            <input type="file" id="editVoiceFile" class="form-input" accept="audio/m4a,audio/mp4,audio/mp3,audio/aac,audio/wav">
            <span class="form-hint">M4A, MP3, AAC, or WAV, up to 20 MB.</span>
          </div>

          <div class="form-group">
            <label class="form-label" for="editBio">Bio</label>
            <textarea id="editBio" class="form-input" rows="3" placeholder="Tell members about yourself...">${escapeHTML(profile.bio || '')}</textarea>
          </div>

          <div class="flex justify-end gap-3 mt-4">
            <button type="button" class="btn btn-secondary" id="cancelEditBtn">Cancel</button>
            <button type="submit" class="btn btn-primary" id="saveProfileBtn">
              <span class="btn-label">Save Profile</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}

/* ------------------------------------------------------------------ */
// Main
/* ------------------------------------------------------------------ */

export function renderSettingsPage(container: HTMLElement): () => void {
  // Auth guard
  if (!authStore.isAuthenticated) {
    container.innerHTML = `
      <main class="section pt-nav text-center">
        <div class="container max-w-md">
          <div class="card card-body">
            <i class="fa-solid fa-lock text-4xl mb-4 text-accent" aria-hidden="true"></i>
            <h2>Sign in Required</h2>
            <p class="text-muted mt-2 mb-6">Please sign in to view and manage your profile.</p>
            <button class="btn btn-primary btn-lg w-full" id="authRedirectBtn" data-route="/auth">
              Sign In or Join
            </button>
          </div>
        </div>
      </main>
    `;

    const btn = container.querySelector('#authRedirectBtn');
    const onRedirect = () => router.navigate('/auth');
    btn?.addEventListener('click', onRedirect);

    return function unmount() {
      btn?.removeEventListener('click', onRedirect);
      container.innerHTML = '';
    };
  }

  // Kick off async render with skeleton
  container.innerHTML = renderLoadingSkeleton();
  const settingsContainer = container as SettingsContainer;
  const renderToken = Symbol('settings-render');
  settingsContainer.__settingsRenderToken = renderToken;
  void renderAccountViewAsync(settingsContainer, renderToken);

  // Return a no-op unmount; the async render will replace content and
  // wire its own cleanup. If the component unmounts before async finishes,
  // the container clear happens in the router layer.
  return function unmount() {
    settingsContainer.__settingsRenderToken = undefined;
    settingsContainer.__settingsCleanup?.();
    delete settingsContainer.__settingsCleanup;
    container.innerHTML = '';
  };
}

async function renderAccountViewAsync(
  container: SettingsContainer,
  renderToken: symbol,
): Promise<void> {
  const user = authStore.user!;
  const name = user.displayName || user.email?.split('@')[0] || 'Member';

  // Fetch profile
  let profile: ProfileData | null = null;
  let fetchError = false;

  try {
    const profileDoc = await getDoc(doc(db, 'profiles', user.uid));
    if (profileDoc.exists()) profile = profileDoc.data() as ProfileData;
  } catch (err) {
    console.error('Failed to load profile:', err);
    fetchError = true;
  }

  if (container.__settingsRenderToken !== renderToken) return;
  container.__settingsCleanup?.();
  delete container.__settingsCleanup;

  const pp = profile || {};
  const photoUrl = pp.photoUrls?.[0] || user.photoURL || '';
  const age = Number(pp.age) || null;
  const bio = pp.bio || '';
  const city = pp.city || '';
  const tribe = pp.tribe || '';
  const religion = pp.religion || '';
  const intent = pp.relationshipIntent || '';
  const gender = pp.gender || '';
  const whatsappNumber = pp.whatsappNumber || '';
  const interests = pp.interests || [];
  const voiceUrl = pp.voiceNoteUrl || '';
  const verified = user.emailVerified || false;
  const memberSince = formatMemberSince(user.metadata.creationTime);

  // Build tag list
  const tags: { icon: string; label: string }[] = [];
  if (city) tags.push({ icon: 'fa-location-dot', label: city });
  if (tribe) tags.push({ icon: 'fa-people-group', label: tribe });
  if (religion) tags.push({ icon: 'fa-mosque', label: religion });
  if (intent) tags.push({ icon: 'fa-heart', label: intent });

  // Render content
  container.innerHTML = `
    <main class="section pt-nav">
      <div class="container max-w-2xl">
        <div class="section-header">
          <h1>My Profile</h1>
          <p class="section-subtitle text-muted">Manage your account, membership, and profile details.</p>
        </div>

        ${fetchError ? `<div class="alert alert-error mb-6" role="alert"><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i> Could not load your full profile. Some details may be missing.</div>` : ''}

        ${renderProfileHeader(name, user.email || '', age, photoUrl, authStore.isPremium, verified, tags)}
        ${renderBioSection(bio, interests)}
        ${voiceUrl ? renderVoicePlayer(voiceUrl) : ''}
        ${renderActions(authStore.isPremium, pp.moderationStatus)}

        <p class="text-center text-xs text-muted">Member since ${memberSince}</p>

        ${renderEditModal(pp, name)}
        <audio id="globalAudioPlayer" hidden></audio>
      </div>
    </main>
  `;

  /* ---------------------------------------------------------------- */
  // Event wiring with cleanup
  /* ---------------------------------------------------------------- */

  const listeners: Array<{ el: EventTarget; type: string; fn: EventListener }> = [];
  let isSubmitting = false;

  function on(
    selector: string,
    type: 'click' | 'submit' | 'keydown',
    handler: EventListener,
    scope: HTMLElement | Document = container,
  ): void {
    const el = scope.querySelector(selector);
    if (!el) return;
    el.addEventListener(type, handler);
    listeners.push({ el, type, fn: handler });
  }

  // Navigation
  on('#viewPlansBtn', 'click', () => router.navigate('/membership'));

  on('#submitProfileBtn', 'click', async () => {
    const submitBtn = container.querySelector('#submitProfileBtn') as HTMLButtonElement;
    submitBtn.disabled = true;
    try {
      const submitProfileForReview = httpsCallable(functions, 'submitProfileForReview');
      await submitProfileForReview();
      showToast('Profile submitted for review.', 'success');
      await renderAccountViewAsync(container, renderToken);
    } catch (err) {
      console.error('Profile submission failed:', err);
      showToast('Add a photo and a bio of at least 10 characters before submitting.', 'error');
      submitBtn.disabled = false;
    }
  });

  on('#signOutBtn', 'click', async () => {
    try {
      await auth.signOut();
      showToast('Signed out.', 'info');
      router.navigate('/');
    } catch (err) {
      console.error('Sign out failed:', err);
      showToast('Could not sign out. Please try again.', 'error');
    }
  });

  // Delete account with confirmation modal (using native confirm as fallback,
  // but with a deliberate double-check pattern)
  on('#deleteAccountBtn', 'click', async () => {
    const step1 = confirm(
      'Are you sure you want to delete your account? This will permanently remove your profile, photos, matches, and messages.',
    );
    if (!step1) return;

    const step2 = confirm(
      'This action is irreversible. Press OK to permanently delete your Zuri account.',
    );
    if (!step2) return;

    const btn = container.querySelector('#deleteAccountBtn') as HTMLButtonElement;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    try {
      const deleteAccountFn = httpsCallable(functions, 'deleteAccount');
      await deleteAccountFn();
      showToast('Your account has been deleted.', 'info');
      router.navigate('/');
    } catch (err) {
      console.error('Account deletion failed:', err);
      showToast(
        'Could not delete account. Please sign out and back in, then try again.',
        'error',
      );
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  });

  // Modal logic with focus management
  const modal = container.querySelector('#editProfileModal') as HTMLElement;
  const editBtn = container.querySelector('#editProfileBtn') as HTMLButtonElement;

  function openModal() {
    modal.hidden = false;
    modal.classList.add('open');
    // Focus first input
    const firstInput = modal.querySelector<HTMLElement>('input, select, textarea');
    firstInput?.focus();
  }

  function closeModal() {
    modal.classList.remove('open');
    modal.hidden = true;
    editBtn?.focus();
  }

  on('#editProfileBtn', 'click', openModal);
  on('#editProfileModalClose', 'click', closeModal);
  on('#cancelEditBtn', 'click', closeModal);

  // Escape to close modal
  const onKeydown = (e: Event) => {
    const ke = e as KeyboardEvent;
    if (ke.key === 'Escape' && !modal.hidden) {
      closeModal();
    }
  };
  document.addEventListener('keydown', onKeydown);
  listeners.push({ el: document, type: 'keydown', fn: onKeydown });

  // Voice player
  const voiceBtn = container.querySelector('#profileVoicePlayBtn') as HTMLButtonElement | null;
  if (voiceBtn) {
    const player = container.querySelector('#globalAudioPlayer') as HTMLAudioElement;
    let isPlaying = false;

    const onVoiceClick = () => {
      const url = voiceBtn.dataset.url || '';
      if (!url || !player) return;

      if (isPlaying) {
        player.pause();
        isPlaying = false;
        voiceBtn.innerHTML = '<i class="fa-solid fa-play" aria-hidden="true"></i>';
        voiceBtn.setAttribute('aria-label', 'Play voice intro');
        return;
      }

      player.src = url;
      player.play().catch((err) => {
        console.error('Audio playback failed:', err);
        showToast('Could not play voice intro.', 'error');
        isPlaying = false;
        voiceBtn.innerHTML = '<i class="fa-solid fa-play" aria-hidden="true"></i>';
      });

      isPlaying = true;
      voiceBtn.innerHTML = '<i class="fa-solid fa-pause" aria-hidden="true"></i>';
      voiceBtn.setAttribute('aria-label', 'Pause voice intro');
    };

    const onAudioEnd = () => {
      isPlaying = false;
      voiceBtn.innerHTML = '<i class="fa-solid fa-play" aria-hidden="true"></i>';
      voiceBtn.setAttribute('aria-label', 'Play voice intro');
    };

    voiceBtn.addEventListener('click', onVoiceClick);
    player?.addEventListener('ended', onAudioEnd);

    listeners.push({ el: voiceBtn, type: 'click', fn: onVoiceClick });
    if (player) listeners.push({ el: player, type: 'ended', fn: onAudioEnd });
  }

  // Form submission
  on('#editProfileForm', 'submit', async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const saveBtn = container.querySelector('#saveProfileBtn') as HTMLButtonElement;
    const form = e.target as HTMLFormElement;

    // Client-side validation
    const editName = (container.querySelector('#editName') as HTMLInputElement).value.trim();
    const editAge = parseInt((container.querySelector('#editAge') as HTMLInputElement).value, 10);
    const editGender = (container.querySelector('#editGender') as HTMLSelectElement).value;
    const editCity = (container.querySelector('#editCity') as HTMLInputElement).value.trim();
    const editWhatsapp = (container.querySelector('#editWhatsapp') as HTMLInputElement).value.trim();
    const editTribe = (container.querySelector('#editTribe') as HTMLSelectElement).value;
    const editReligion = (container.querySelector('#editReligion') as HTMLSelectElement).value;
    const editIntent = (container.querySelector('#editIntent') as HTMLSelectElement).value;
    const photoFile = (container.querySelector('#editPhotoFile') as HTMLInputElement).files?.[0];
    const voiceFile = (container.querySelector('#editVoiceFile') as HTMLInputElement).files?.[0];
    const editBio = (container.querySelector('#editBio') as HTMLTextAreaElement).value.trim();

    if (!editName || editName.length < 2) {
      showToast('Display name must be at least 2 characters.', 'error');
      (container.querySelector('#editName') as HTMLInputElement).focus();
      return;
    }
    if (isNaN(editAge) || editAge < 18 || editAge > 99) {
      showToast('Please enter a valid age between 18 and 99.', 'error');
      (container.querySelector('#editAge') as HTMLInputElement).focus();
      return;
    }
    if (!editGender) {
      showToast('Please select a gender.', 'error');
      (container.querySelector('#editGender') as HTMLSelectElement).focus();
      return;
    }
    if (!editCity) {
      showToast('Please enter your city or state.', 'error');
      (container.querySelector('#editCity') as HTMLInputElement).focus();
      return;
    }

    isSubmitting = true;
    saveBtn.disabled = true;
    saveBtn.setAttribute('aria-busy', 'true');
    const originalLabel = saveBtn.querySelector('.btn-label')?.textContent || 'Save Profile';
    const labelSpan = saveBtn.querySelector('.btn-label');
    if (labelSpan) labelSpan.textContent = 'Saving…';

    try {
      let photoUrl = profile?.photoUrls?.[0] || '';
      let voiceUrl = profile?.voiceNoteUrl || '';

      if (photoFile) {
        const photoRef = ref(storage, `users/${user.uid}/photos/${crypto.randomUUID()}.jpg`);
        await uploadBytes(photoRef, photoFile, { contentType: photoFile.type });
        photoUrl = await getDownloadURL(photoRef);
      }

      if (voiceFile) {
        const voiceRef = ref(storage, `users/${user.uid}/voice-intros/${crypto.randomUUID()}.m4a`);
        await uploadBytes(voiceRef, voiceFile, { contentType: voiceFile.type });
        voiceUrl = await getDownloadURL(voiceRef);
      }

      // Update Firebase Auth profile
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: editName,
          photoURL: photoUrl || undefined,
        });
      }

      const sharedFields = {
        displayName: editName,
        age: editAge,
        city: editCity,
        whatsappNumber: editWhatsapp,
        tribe: editTribe,
        religion: editReligion,
        relationshipIntent: editIntent,
        bio: editBio,
      };

      // Backend callable for audit + validation
      if (profile) {
        const updateProfileFn = httpsCallable(functions, 'updateProfile');
        await updateProfileFn(sharedFields);
      } else {
        const createProfileFn = httpsCallable(functions, 'createProfile');
        await createProfileFn({ ...sharedFields, gender: editGender });
      }

      // Direct merge for fields not yet in callable allow-list
      await setDoc(
        doc(db, 'profiles', user.uid),
        {
          photoUrls: photoUrl ? [photoUrl] : [],
          voiceNoteUrl: voiceUrl,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      showToast('Profile updated successfully!', 'success');
      closeModal();
      void renderAccountViewAsync(container, renderToken);
    } catch (err) {
      console.error('Failed to update profile:', err);
      showToast('Could not save profile. Please try again.', 'error');
    } finally {
      isSubmitting = false;
      saveBtn.disabled = false;
      saveBtn.removeAttribute('aria-busy');
      if (labelSpan) labelSpan.textContent = originalLabel;
    }
  });

  /* ---------------------------------------------------------------- */
  // Cleanup registration
  /* ---------------------------------------------------------------- */

  // Store cleanup on container so renderSettingsPage can return it
  const cleanup = () => {
    listeners.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
  };

  container.__settingsCleanup = cleanup;
}
